import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Alert, Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {EntityUserCarePlansRepository} from '../../../repositories/entity-user-care-plans';
import {CarePlan} from '../../../backend/interfaces/care-plan';
import {ActivitiesGroup} from '../../../backend/interfaces/activities-group';
import {useSnackBar} from '../../../contexts/snack-bar/context';
import {CarePlanActivitiesGroup} from '../../../backend/interfaces/care-plan-activities-group';
import {CarePlanSheetHeader} from '../header/care-plan-sheet-header';
import {CarePlanSheetActivitiesGroupEntry} from '../activities-group-entry/activities-group-entry';
import {ActivitySkeleton} from '../activities-group-entry/skeleton';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {ActivitiesSector} from '../../../backend/interfaces/activities-sector';
import {useOffice} from '../../../contexts/office/context';
import {useObserveViewport} from '../../../hooks';
import {
  PERSONAL_DEVELOPMENT_SECTOR_ID,
  SECTOR_ID_TO_PERMISSION_TYPE_MAPPING,
  appSizes,
} from '../../../utils/constants';
import {convertArrayToIndex} from '../../../utils/functions';
import {LoadingConfigurationsType} from '../../../enums';
import {useAuth} from '../../../contexts/auth/context';
import MissingAccessScreen from '../../missing-access-screen';
import {TemplateAction, TemplatesModal} from '../templates-modal';
import {EntityTemplatesRepository} from '../../../repositories/entity-templates';
import {EntityTemplate} from '../../../backend/responses/entity-template';
import {DeleteCarePlanModal} from '../delete-careplan-modal';
import {AiTalk} from '../../ai-talk/ai-talk';
import {useFetchCompleteList} from '../../../hooks/fetch-all-items';

const SKELETONS = new Array(3).fill(1);

interface CarePlanSheetProps {
  seniorUserId: string;
  activitiesSectors?: ActivitiesSector[] | null;
  hasDCMaccess: boolean;
}

export const CarePlanSheet = ({
  seniorUserId,
  activitiesSectors,
  hasDCMaccess,
}: CarePlanSheetProps) => {
  // Get params
  let {entityId, activitiesSectorId}: {entityId: string; activitiesSectorId: string} = useParams();
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  const {t} = useTranslation();
  const {isRunningMobile} = useObserveViewport();
  const {hasWritePermission} = useAuth();
  const canWriteCarePlanSector = hasWritePermission(
    SECTOR_ID_TO_PERMISSION_TYPE_MAPPING[activitiesSectorId]
  );

  // Hold state
  const [carePlan, setCarePlan] = useState<CarePlan | undefined | null>(undefined);
  const [carePlaLoadingConfigurations, setCarePlanLoadingConfigurations] =
    useState<LoadingConfigurationsType>(LoadingConfigurationsType.Loading);
  const [onEditMode, setEditMode] = useState<string | undefined>(undefined);
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState<TemplateAction | undefined>();
  const [isSubmitting, setIsSubmitting] = useState<TemplateAction | boolean | undefined>();
  const [isDeletingCarePlan, setIsDeletingCarePlan] = useState<boolean>(false);
  const [templates, setTemplates] = useState<EntityTemplate[]>([]);
  const [genAiExplanation, setGenAiExplanation] = useState<string | null>(null);

  const handleCarePlanSave = (updatedCarePlan: CarePlan) => {
    EntityUserCarePlansRepository.instance
      .updateCarePlan(entityId, seniorUserId, activitiesSectorId, updatedCarePlan)
      .then((response) => {
        // Update context
        setCarePlan(response);

        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(
            translations.seniors.edit.senior_dcm.careplan.on_update.snackbar.success_message
          ),
        });
      })
      .catch((_error) => {
        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.seniors.edit.senior_dcm.careplan.on_update.snackbar.error_message
          ),
        });
      });
  };

  const handleCarePlanDelete = () => {
    setIsSubmitting(true);
    EntityUserCarePlansRepository.instance
      .deleteCarePlan(entityId, seniorUserId, activitiesSectorId)
      .then((_response) => {
        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(
            translations.seniors.edit.senior_dcm.careplan.on_delete.snackbar.success_message
          ),
        });
        setIsDeletingCarePlan(false);
        setIsSubmitting(undefined);
        setCarePlan(
          EntityUserCarePlansRepository.instance.initCarePlan(
            entityId,
            seniorUserId,
            activitiesSectorId
          )
        );
      })
      .catch((_error) => {
        setIsDeletingCarePlan(false);
        setIsSubmitting(undefined);
        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.seniors.edit.senior_dcm.careplan.on_delete.snackbar.error_message
          ),
        });
      });
  };

  const handleImportTemplate = (
    name: string,
    templateId: string,
    activitiesGroupsIds: string[]
  ) => {
    setIsSubmitting('import');
    EntityTemplatesRepository.instance
      .importTemplate(entityId, templateId, seniorUserId, activitiesSectorId, activitiesGroupsIds)
      .then((response) => {
        setIsTemplatesModalOpen(undefined);
        setIsSubmitting(undefined);
        setCarePlan(response);
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.activities_type_entry.templates.snackbar.import_success_message, {
            name,
          }),
        });
      })
      .catch((_error) => {
        setIsTemplatesModalOpen(undefined);
        setIsSubmitting(undefined);
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.activities_type_entry.templates.snackbar.import_error_message, {
            name,
          }),
        });
      });
  };

  const handleSaveTemplate = (name: string, description: string, activitiesGroupsIds: string[]) => {
    setIsSubmitting('save');
    EntityTemplatesRepository.instance
      .createTemplate(
        entityId,
        seniorUserId,
        name,
        activitiesSectorId,
        activitiesGroupsIds,
        description
      )
      .then((response) => {
        setIsTemplatesModalOpen(undefined);
        setIsSubmitting(undefined);
        setTemplates([...templates, response]);
        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.activities_type_entry.templates.snackbar.success_message, {name}),
        });
      })
      .catch((_error) => {
        setIsTemplatesModalOpen(undefined);
        setIsSubmitting(undefined);
        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.activities_type_entry.templates.snackbar.error_message, {name}),
        });
      });
  };

  const handleDeleteEntry = (activitiesGroupEntry: CarePlanActivitiesGroup) => {
    if (!carePlan) return;
    // Remove from care plan
    if (carePlan?.activitiesGroups) {
      carePlan.activitiesGroups = carePlan.activitiesGroups.filter(
        (entry) => entry.id !== activitiesGroupEntry.id
      );
      setCarePlan({
        ...carePlan,
      });
    }
  };

  const handleUpdateEntry = (updatedEntry: CarePlanActivitiesGroup) => {
    if (!carePlan) return;

    // Update entry
    if (carePlan.activitiesGroups) {
      carePlan.activitiesGroups = carePlan.activitiesGroups.map((entry) => {
        if (entry.id === updatedEntry.id) return updatedEntry;
        return entry;
      });
      setCarePlan({
        ...carePlan,
      });
    }
  };

  const handleCreateActivityGroup = (activitiesGroup: ActivitiesGroup) => {
    EntityActivitiesRepository.instance
      .getGroupCarePlanConfigurations(
        entityId,
        activitiesGroup.activitiesSectorId,
        activitiesGroup?.id ?? ''
      )
      .then((response) => {
        EntityUserCarePlansRepository.instance
          .addActivitiesGroupToCarePlan(
            entityId,
            seniorUserId,
            activitiesGroup.activitiesSectorId,
            {
              activitiesGroupId: activitiesGroup?.id ?? '',
              configurations: response
                ? response.map((configuration) => {
                    return {
                      carePlanConfigurationId: configuration.id ?? '',
                      carePlanConfigurationValue: null,
                    };
                  })
                : undefined,
            }
          )
          .then((response) => {
            if (response) handleAddedEntrySideEffect(response);
            snackBarContext.setOfficeToastNotification({
              severity: 'success',
              message: t(translations.activities_group_entry.snackbar.create.success),
            });
          })
          .catch((_error) => {
            snackBarContext.setOfficeToastNotification({
              severity: 'error',
              message: t(translations.activities_group_entry.snackbar.create.error),
            });
          });
      })
      .catch((_error) => {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.activities_group_entry.snackbar.create.error),
        });
      });
  };

  // Load all activities groups
  const {items: activitiesGroups, isLoading: isLoadingActivitiesGroups} = useFetchCompleteList({
    fetchFunction: () => {
      if (!hasDCMaccess && activitiesSectorId !== PERSONAL_DEVELOPMENT_SECTOR_ID) {
        return Promise.resolve([]);
      }

      return EntityActivitiesRepository.instance.getAllGroups(entityId, activitiesSectorId);
    },
    deps: [activitiesSectorId],
  });

  // Memo them indexed
  const indexedActivitiesGroup = useMemo(() => {
    if (!activitiesGroups) {
      return;
    }

    return convertArrayToIndex<ActivitiesGroup>(activitiesGroups, 'id');
  }, [activitiesGroups]);

  // Load the care plan
  const loadCarePlan = useCallback(() => {
    // Unset previous
    setCarePlan(undefined);

    if (!hasDCMaccess && activitiesSectorId !== PERSONAL_DEVELOPMENT_SECTOR_ID) {
      return;
    }

    setCarePlanLoadingConfigurations(LoadingConfigurationsType.Loading);

    EntityUserCarePlansRepository.instance
      .getCarePlan(entityId, seniorUserId, activitiesSectorId)
      .then((carePlan) => {
        if (!carePlan) {
          // Does not exist yet on the backend
          // Initialize empty
          setCarePlan(
            EntityUserCarePlansRepository.instance.initCarePlan(
              entityId,
              seniorUserId,
              activitiesSectorId
            )
          );

          // Log
          console.debug(
            `Care Plan doesn't exist yet on the backend. Creating one now for ${entityId}/${seniorUserId}/${activitiesSectorId}`
          );
        } else {
          // Set data
          setCarePlan(carePlan);
        }

        setCarePlanLoadingConfigurations(LoadingConfigurationsType.Loaded);
      })
      .catch((err) => {
        console.error(`Error: ${err}`);
      });

    // Load templates
    EntityTemplatesRepository.instance
      .getAllTemplates(entityId, activitiesSectorId)
      .then((response) => {
        setTemplates(response);
      })
      .catch((err) => {
        console.error(`Error: ${err}`);
      });
  }, [hasDCMaccess, activitiesSectorId, entityId, seniorUserId]);

  // Load the care plan
  const aiGenerateCarePlan = useCallback(() => {
    // Unset previous
    setCarePlan(undefined);
    setGenAiExplanation(null);

    if (!hasDCMaccess && activitiesSectorId !== PERSONAL_DEVELOPMENT_SECTOR_ID) {
      return;
    }

    setCarePlanLoadingConfigurations(LoadingConfigurationsType.Loading);

    EntityUserCarePlansRepository.instance
      .generateCarePlanByAi(entityId, seniorUserId, activitiesSectorId)
      .then((data: {explanation: string; carePlan: CarePlan}) => {
        if (!data.carePlan) {
          // Log
          console.debug(
            `Care Plan was not generated by AI for ${entityId}/${seniorUserId}/${activitiesSectorId}`
          );
        } else {
          // Set data
          setGenAiExplanation(data.explanation);
          setCarePlan(data.carePlan);
        }

        setCarePlanLoadingConfigurations(LoadingConfigurationsType.Loaded);
      })
      .catch((err) => {
        console.error(`Error: ${err}`);
      });

    // Load templates
    EntityTemplatesRepository.instance
      .getAllTemplates(entityId, activitiesSectorId)
      .then((response) => {
        setTemplates(response);
      })
      .catch((err) => {
        console.error(`Error: ${err}`);
      });
  }, [hasDCMaccess, activitiesSectorId, entityId, seniorUserId]);

  // We're still loading data if either activities groups or the care plan aren't loaded yet
  const loadingConfigurations = useMemo(() => {
    if (
      isLoadingActivitiesGroups ||
      carePlaLoadingConfigurations === LoadingConfigurationsType.Loading
    ) {
      return LoadingConfigurationsType.Loading;
    }

    return LoadingConfigurationsType.Loaded;
  }, [isLoadingActivitiesGroups, carePlaLoadingConfigurations]);

  const handleAddedEntrySideEffect = (carePlanActivitiesGroup: CarePlanActivitiesGroup) => {
    // unset previous
    setEditMode(undefined);

    if (!carePlan) return;

    // shallow copy the current careplan
    const updatedCareplan = {...carePlan};
    if (!updatedCareplan.activitiesGroups) updatedCareplan.activitiesGroups = [];

    // add new entry as first
    updatedCareplan.activitiesGroups?.unshift(carePlanActivitiesGroup);

    // set entry to current careplan
    setCarePlan(updatedCareplan);

    // focus on new entry
    setEditMode(carePlanActivitiesGroup.id);
  };

  const calculatedContainerMaxWidth = useMemo(() => {
    let sum = 0;

    let appContainersMargins = 16 * 2;

    // hamburguer
    if (!isRunningMobile) {
      if (officeContext.state.drawerOpened && !officeContext.state.drawerCollapsed) {
        sum += appSizes.sideHamburguerMenu.width.open;
      } else {
        sum += appSizes.sideHamburguerMenu.width.closed;
      }
    } else if (officeContext.state.drawerCollapsed) {
      appContainersMargins += 16;
    }

    // sidemenu
    if (!officeContext.state.sideNavigationDrawerCollapsed) {
      sum += appSizes.genericCollapsibleSidePanel.width.open;
    } else {
      sum += appSizes.genericCollapsibleSidePanel.width.closed;
    }

    return `calc(100vw - ${sum}px - ${appContainersMargins}px)`;
  }, [
    isRunningMobile,
    officeContext.state.drawerCollapsed,
    officeContext.state.drawerOpened,
    officeContext.state.sideNavigationDrawerCollapsed,
  ]);

  const onActivityGroupEditFocusSlideCardBehaviour = useCallback(() => {
    const card = document.getElementById(`activity_card_${onEditMode}`);

    if (card) {
      const offset = 75;
      const cardPosition = card.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = cardPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, [onEditMode]);

  useEffect(() => {
    loadCarePlan();
  }, [activitiesSectorId, loadCarePlan]);

  useEffect(() => {
    if (onEditMode) onActivityGroupEditFocusSlideCardBehaviour();
  }, [onActivityGroupEditFocusSlideCardBehaviour, onEditMode]);

  // reset editMode on `activitiesSectorId` change
  useEffect(() => {
    setEditMode(undefined);
  }, [activitiesSectorId]);

  if (!hasDCMaccess && activitiesSectorId !== PERSONAL_DEVELOPMENT_SECTOR_ID) {
    return <MissingAccessScreen denied={'care_management'} />;
  }

  return (
    <Stack
      rowGap={2}
      sx={{
        maxWidth: calculatedContainerMaxWidth,
        transition: 'max-width 0.2s ease-in-out',
      }}
    >
      {/* HEADER */}
      <CarePlanSheetHeader
        isLoading={loadingConfigurations === LoadingConfigurationsType.Loading}
        activitiesSectorId={activitiesSectorId}
        activitiesSectors={activitiesSectors}
        activitiesGroups={activitiesGroups}
        carePlan={carePlan}
        onCarePlanSave={handleCarePlanSave}
        onCarePlanDelete={() => setIsDeletingCarePlan(true)}
        onSaveTemplate={() => setIsTemplatesModalOpen('save')}
        onImportTemplate={() => setIsTemplatesModalOpen('import')}
        onActivitiesGroupSelected={(activitiesGroup: ActivitiesGroup) =>
          handleCreateActivityGroup(activitiesGroup)
        }
        canWrite={canWriteCarePlanSector}
        hasTemplates={templates.length > 0}
        onAiGeneratedRequest={() => {
          aiGenerateCarePlan();
        }}
      />

      {isTemplatesModalOpen && (
        <TemplatesModal
          open
          entry={carePlan}
          action={isTemplatesModalOpen}
          allActivitiesGroup={activitiesGroups}
          submitting={isSubmitting as TemplateAction}
          templates={templates}
          onSave={handleSaveTemplate}
          onImport={handleImportTemplate}
          onClose={() => {
            setIsSubmitting(undefined);
            setIsTemplatesModalOpen(undefined);
          }}
        />
      )}

      {isDeletingCarePlan && (
        <DeleteCarePlanModal
          open
          entry={carePlan}
          submitting={!!isSubmitting}
          onClose={() => {
            setIsSubmitting(undefined);
            setIsDeletingCarePlan(false);
          }}
          onDelete={handleCarePlanDelete}
        />
      )}

      {/* RESULTS */}
      {loadingConfigurations === LoadingConfigurationsType.Loading && (
        <Box className="result-wrapper">
          {SKELETONS.map((_props, index) => (
            <ActivitySkeleton key={`activity_skeleton_${index}`} />
          ))}
        </Box>
      )}

      {loadingConfigurations === LoadingConfigurationsType.Loaded && !carePlan && (
        <Alert severity="error">
          {t(translations.seniors.edit.senior_dcm.careplan.load_error)}
        </Alert>
      )}

      {genAiExplanation && <AiTalk content={genAiExplanation} />}

      {loadingConfigurations === LoadingConfigurationsType.Loaded &&
        (!carePlan?.activitiesGroups || carePlan?.activitiesGroups.length === 0) && (
          <Box className="result-wrapper result-wrapper-center">
            <Box className="result-wrapper-empty">
              <h3 className="title">
                {t(translations.seniors.edit.senior_dcm.careplan.activities.empty_state.title)}
              </h3>
              <h5 className="subtitle">
                {t(translations.seniors.edit.senior_dcm.careplan.activities.empty_state.subtitle)}
              </h5>
            </Box>
          </Box>
        )}

      {loadingConfigurations === LoadingConfigurationsType.Loaded &&
        carePlan?.activitiesGroups &&
        indexedActivitiesGroup &&
        carePlan?.activitiesGroups.length > 0 && (
          <Box className="result-wrapper">
            {carePlan.activitiesGroups.map(
              (activitiesGroupEntry: CarePlanActivitiesGroup, index: number) => {
                // Extract display order from activity group configuration
                const displayOrder =
                  indexedActivitiesGroup[activitiesGroupEntry.activitiesGroupId]?.displayOrder || 1;

                return (
                  <Box
                    id={`activities_group_entry_card_${activitiesGroupEntry.id}`}
                    sx={{order: displayOrder}}
                    key={`activities-group-entry-container-${activitiesGroupEntry.id}_${activitiesSectorId}`}
                  >
                    <CarePlanSheetActivitiesGroupEntry
                      entry={activitiesGroupEntry}
                      seniorUserId={seniorUserId}
                      activitiesSectorId={activitiesSectorId}
                      onDelete={() => handleDeleteEntry(activitiesGroupEntry)}
                      onUpdate={handleUpdateEntry}
                      onEditMode={onEditMode === activitiesGroupEntry.id}
                      setEditMode={setEditMode}
                    />
                  </Box>
                );
              }
            )}
          </Box>
        )}
    </Stack>
  );
};
